import { Check, Bot } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Breadcrumb } from '@/components/common/Breadcrumb';
import { PageHeader } from '@/components/common/PageHeader';
import { QueryErrorView } from '@/components/common/QueryErrorView';
import { Can } from '@/components/permission/Can';
import { DataTable, type DataTableColumn } from '@/components/shared/DataTable';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatDate } from '@/features/common/format';
import { useGetOrgProjectsQuery } from '@/features/orgs/api/orgApi';

import { useGetFactoriesQuery, useGetModelCollectionsQuery } from '../api/agentsApi';
import type { AgentFactoryView } from '../types';
import { memoryCollectionIds, docId } from '../utils';

const ALL_PROJECTS = 'all';

/**
 * Agents list (design §19 — minimal T6 contract on the P1 list pattern):
 * `GET /agent/factories?orgId=` (the P2-T1 org filter) + client-side project
 * filtering (the list DTO only carries `orgId` — no invented query params).
 * "New agent" enters the gallery with `?projectId=` preserved (§5). NOTE: the
 * factory view has no `kind` field (kind lives on the template and is not
 * persisted at create) — the §19 kind column is intentionally omitted rather
 * than invented.
 */
export function AgentsListPage() {
  const { orgId = '' } = useParams<{ orgId: string }>();
  const [searchParams] = useSearchParams();
  const projectFilter = searchParams.get('projectId') ?? ALL_PROJECTS;
  const navigate = useNavigate();

  const { data: factories, isLoading, isError, error, refetch } = useGetFactoriesQuery({ orgId });
  const { data: modelCollections } = useGetModelCollectionsQuery();
  const { data: projects } = useGetOrgProjectsQuery(orgId);

  const [internalProjectFilter, setInternalProjectFilter] = useState<string>(projectFilter);
  const selectedProject = internalProjectFilter;

  const filtered = useMemo(() => {
    const rows = factories ?? [];
    return selectedProject === ALL_PROJECTS
      ? rows
      : rows.filter((factory) => factory.projectId === selectedProject);
  }, [factories, selectedProject]);

  const modelNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const collection of modelCollections ?? []) {
      map.set(collection.id, collection.name);
    }
    return map;
  }, [modelCollections]);

  const galleryPath = `/organizations/${orgId}/agents/new${
    selectedProject !== ALL_PROJECTS ? `?projectId=${encodeURIComponent(selectedProject)}` : ''
  }`;

  const columns: DataTableColumn<AgentFactoryView>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (factory) => <span className="font-medium">{factory.name}</span>,
    },
    {
      id: 'model',
      header: 'Model',
      cell: (factory) => (
        <span className="text-muted-foreground">
          {modelNameById.get(factory.modelCollectionId) ?? factory.modelCollectionId}
        </span>
      ),
    },
    {
      id: 'memory',
      header: 'Memory',
      cell: (factory) =>
        memoryCollectionIds(factory).length > 0 ? (
          <span className="inline-flex items-center gap-1 text-sm">
            <Check className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">Memory attached</span>
          </span>
        ) : (
          <span aria-hidden="true">—</span>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (factory) =>
        factory.isActive ? (
          <Badge variant="success">Active</Badge>
        ) : (
          <Badge variant="secondary">Inactive</Badge>
        ),
    },
    {
      id: 'updated',
      header: 'Updated',
      cell: (factory) => (
        <span className="text-sm text-muted-foreground">{formatDate(factory.updatedAt)}</span>
      ),
    },
  ];

  return (
    <section aria-labelledby="agents-title" className="space-y-6">
      <Breadcrumb
        items={[{ label: 'Organization', to: `/organizations/${orgId}` }, { label: 'Agents' }]}
      />

      <PageHeader
        title="Agents"
        description="Agents in this organization."
        titleId="agents-title"
        actions={
          <Can action="agent-factory:templates:read" resource="agent-factory:templates">
            <Button onClick={() => navigate(galleryPath)}>New agent</Button>
          </Can>
        }
      />

      {(projects ?? []).length > 0 && (
        <div className="max-w-xs">
          <Select value={selectedProject} onValueChange={setInternalProjectFilter}>
            <SelectTrigger aria-label="Filter by project">
              <SelectValue placeholder="All projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_PROJECTS}>All projects</SelectItem>
              {(projects ?? []).map((project) => (
                <SelectItem key={docId(project)} value={docId(project)}>
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {isLoading ? (
        <LoadingRows label="Loading agents" columns={4} />
      ) : isError ? (
        <QueryErrorView error={error} label="agents" onRetry={() => void refetch()} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Bot />}
          title="No agents"
          description="Create one from a template."
          action={
            <Can action="agent-factory:templates:read" resource="agent-factory:templates">
              <Button variant="outline" onClick={() => navigate(galleryPath)}>
                New agent
              </Button>
            </Can>
          }
        />
      ) : (
        <DataTable
          columns={columns}
          data={filtered}
          getRowId={(factory) => factory.id}
          onRowClick={(factory) => navigate(`/organizations/${orgId}/agents/${factory.id}`)}
        />
      )}
    </section>
  );
}
